import { Form, Formik, type FormikHelpers } from "formik";
import { ArrowRight, Check, Download, Plus, Users, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { PageHeader } from "@/components/common/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type BusinessPractitioner,
  createBusinessPractitioner,
  listBusinessPractitioners,
} from "@/features/business-practitioners/api";
import {
  assignNsiMember,
  type BpTeamMember,
  getBpTeam,
  unassignNsiMember,
} from "@/features/business-practitioners/team-api";
import { canAssignRole, isPlatformUser, orgRoleRank } from "@/features/auth/roles";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useFirmContextStore } from "@/features/firm-context/store/firm-context-store";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { listMembers, type Member } from "@/features/team/api";
import { ApiError } from "@/lib/api";
import { applyApiErrors } from "@/lib/form-errors";
import { cn } from "@/lib/utils";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Two-letter initials from a firm name, for the avatar fallback. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  return (last ? first.charAt(0) + last.charAt(0) : first.slice(0, 2)).toUpperCase();
}

/** Short relative label for when a firm joined. */
function joinedLabel(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  return new Date(iso).toLocaleDateString();
}

/** Counts of firms joined per week over the last 8 weeks, oldest → newest. */
function weeklyJoinCounts(list: BusinessPractitioner[]): number[] {
  const now = Date.now();
  const buckets = new Array(8).fill(0);
  for (const bp of list) {
    const week = Math.floor((now - new Date(bp.created_at).getTime()) / WEEK_MS);
    if (week >= 0 && week < 8) buckets[7 - week] += 1;
  }
  return buckets;
}

/** Download the current firm list as a CSV (client-side, no server round-trip). */
function exportCsv(rows: BusinessPractitioner[]) {
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const header = ["Firm", "Contact email", "Members", "Joined"];
  const lines = rows.map((r) =>
    [r.name, r.email || "", String(r.member_count), new Date(r.created_at).toISOString().slice(0, 10)]
      .map(escape)
      .join(","),
  );
  const csv = [header.join(","), ...lines].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "business-practitioners.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function BusinessPractitionersDirectory() {
  const navigate = useNavigate();
  const setActiveFirm = useFirmContextStore((s) => s.setActiveFirm);
  const [list, setList] = useState<BusinessPractitioner[] | null>(null);
  const [error, setError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [managingTeam, setManagingTeam] = useState<BusinessPractitioner | undefined>();
  const [filter, setFilter] = useState<"all" | "nomembers">("all");
  // Only GA/SA provision BPs; a Country Director / Team Leader manages the team of
  // the BPs already assigned to them. A house Staff member only reaches this view
  // via a BP assignment and can view/open its clients — not manage its team.
  const canProvision = useAuthStore((s) => isPlatformUser(s.user));
  const canManageTeams = useAuthStore(
    (s) => isPlatformUser(s.user) || orgRoleRank(s.user) >= 2,
  );

  useEffect(() => {
    let active = true;
    listBusinessPractitioners()
      .then((bps) => active && setList(bps))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  async function refresh() {
    try {
      setList(await listBusinessPractitioners());
    } catch {
      setError(true);
    }
  }

  // Everything below is derived from the loaded list — the firms API returns no
  // status/plan/client-count, so the console surfaces only what the data supports.
  const sorted = useMemo(
    () =>
      list
        ? [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        : [],
    [list],
  );
  const seats = useMemo(() => sorted.reduce((sum, bp) => sum + bp.member_count, 0), [sorted]);
  // "Needs attention" = firms with no members yet (nobody can work the firm).
  // The API doesn't expose a director, so this is the honest derivable signal.
  const noMembers = useMemo(() => sorted.filter((bp) => bp.member_count === 0), [sorted]);
  const firmsThisWeek = useMemo(
    () => sorted.filter((bp) => Date.now() - new Date(bp.created_at).getTime() < WEEK_MS).length,
    [sorted],
  );
  const weeklyJoins = useMemo(() => weeklyJoinCounts(sorted), [sorted]);

  const firms = sorted.length;
  // One decimal only when it isn't whole, so small counts never read as a
  // contradiction (e.g. 1 seat across 2 firms → "avg 0.5", not "avg 0").
  const avg = firms ? seats / firms : 0;
  const avgPerFirm = Number.isInteger(avg) ? String(avg) : avg.toFixed(1);
  const noMembersCount = noMembers.length;
  const newest = sorted[0];
  const rows = filter === "nomembers" ? noMembers : sorted;

  function openClients(bp: BusinessPractitioner) {
    setActiveFirm(bp.id, bp.name);
    navigate("/clients");
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform · Firms"
        title="Business Practitioners"
        subtitle={
          <>
            Every firm running on NSI Capitals —{" "}
            <strong className="text-foreground font-semibold">
              {firms} firms, {seats} seats
            </strong>
            {noMembersCount > 0 && (
              <>
                , and{" "}
                <strong className="text-warning font-semibold">
                  {noMembersCount} with no members yet
                </strong>
              </>
            )}
            .
          </>
        }
        actions={
          canProvision && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={firms === 0}
                onClick={() => exportCsv(sorted)}
              >
                <Download className="size-4" />
                Export CSV
              </Button>
              <Button
                type="button"
                variant="cta"
                size="sm"
                className="gap-1.5"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="size-4" />
                New Business Practitioner
              </Button>
            </>
          )
        }
      />

      {error ? (
        <p className="text-destructive text-sm">Couldn&apos;t load Business Practitioners.</p>
      ) : (
        <>
          {/* Pulse band — derived counts only */}
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile
              label="Firms on platform"
              value={firms}
              loading={!list}
              sub={firmsThisWeek > 0 ? `+${firmsThisWeek} this week` : "No new firms this week"}
              subClassName={firmsThisWeek > 0 ? "text-success font-semibold" : undefined}
            />
            <StatTile
              label="Seats in use"
              value={seats}
              loading={!list}
              sub={firms ? `across all firms · avg ${avgPerFirm} per firm` : "No seats yet"}
            />
            <StatTile
              label="Need attention"
              value={noMembersCount}
              loading={!list}
              tone="warning"
              sub={noMembersCount > 0 ? "firms with no members yet" : "every firm has members"}
            />
          </div>

          {/* Table + side rail */}
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
            <Card className="gap-0 overflow-hidden p-0">
              <div className="flex flex-wrap items-center gap-3 border-b p-4">
                <Tabs value={filter} onValueChange={(v) => setFilter(v as "all" | "nomembers")}>
                  <TabsList>
                    <TabsTrigger value="all">
                      All <span className="text-muted-foreground">{firms}</span>
                    </TabsTrigger>
                    <TabsTrigger value="nomembers">
                      No members{" "}
                      <span className={noMembersCount > 0 ? "text-warning" : "text-muted-foreground"}>
                        {noMembersCount}
                      </span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Firm</TableHead>
                    <TableHead>Contact email</TableHead>
                    <TableHead className="w-28 text-right">Members</TableHead>
                    <TableHead className="w-40">Joined</TableHead>
                    <TableHead className="w-56 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!list ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground text-center">
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground text-center">
                        {filter === "nomembers"
                          ? "Every firm has members."
                          : "No Business Practitioners yet."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((bp) => (
                      <TableRow key={bp.id}>
                        <TableCell className="font-medium">
                          <span className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback className="bg-info-soft text-info font-heading text-xs font-bold">
                                {initials(bp.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="flex flex-col leading-tight">
                              <span className="flex items-center gap-1.5">
                                {bp.name}
                                {bp.code && (
                                  <Badge variant="outline" className="font-normal">
                                    {bp.code}
                                  </Badge>
                                )}
                              </span>
                              <span className="text-muted-foreground text-xs font-normal">
                                Business practitioner
                              </span>
                            </span>
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {bp.email || "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{bp.member_count}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(bp.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canManageTeams && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                onClick={() => setManagingTeam(bp)}
                              >
                                <Users className="size-4" />
                                Team
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1.5"
                              onClick={() => openClients(bp)}
                            >
                              Clients
                              <ArrowRight className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {list && firms > 0 && (
                <div className="text-muted-foreground border-t p-4 text-sm">
                  Showing <strong className="text-foreground">{rows.length}</strong> of {firms} firms
                </div>
              )}
            </Card>

            <aside className="flex flex-col gap-4">
              <AttentionCard
                loading={!list}
                noMembersCount={noMembersCount}
                onReview={() => setFilter("nomembers")}
              />
              <Card className="flex flex-col gap-3 p-5">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium">Firms joined</span>
                  <span className="text-muted-foreground text-xs">Last 8 weeks</span>
                </div>
                <FirmsJoinedChart weeks={weeklyJoins} />
                <p className="text-muted-foreground text-xs">
                  {weeklyJoins.reduce((a, b) => a + b, 0)} new{" "}
                  {weeklyJoins.reduce((a, b) => a + b, 0) === 1 ? "firm" : "firms"} in this window.
                </p>
              </Card>
              {newest && (
                <NewestFirmCard bp={newest} onOpenTeam={() => setManagingTeam(newest)} />
              )}
            </aside>
          </div>
        </>
      )}

      <CreateBusinessPractitionerDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refresh}
      />

      {managingTeam && (
        <ManageTeamDialog bp={managingTeam} onClose={() => setManagingTeam(undefined)} />
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
  subClassName,
  tone,
  loading,
}: {
  label: string;
  value: number;
  sub: string;
  subClassName?: string;
  tone?: "warning";
  loading: boolean;
}) {
  return (
    <Card
      className={cn("gap-1.5 p-5", tone === "warning" && "bg-warning-soft border-warning/20")}
    >
      <span
        className={cn(
          "text-xs font-semibold tracking-wide uppercase",
          tone === "warning" ? "text-warning" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
      {loading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <span
          className={cn(
            "font-heading text-3xl leading-none font-bold tabular-nums",
            tone === "warning" && "text-warning",
          )}
        >
          {value}
        </span>
      )}
      {!loading && (
        <span
          className={cn(
            "text-xs",
            subClassName ?? (tone === "warning" ? "text-warning" : "text-muted-foreground"),
          )}
        >
          {sub}
        </span>
      )}
    </Card>
  );
}

/** Navy attention panel — surfaces the one real signal we can derive. */
function AttentionCard({
  noMembersCount,
  onReview,
  loading,
}: {
  noMembersCount: number;
  onReview: () => void;
  loading: boolean;
}) {
  return (
    <div className="bg-brand-panel relative overflow-hidden rounded-xl p-5 text-white">
      <span className="text-brand-panel-muted text-xs font-semibold tracking-wide uppercase">
        Needs your attention
      </span>
      {loading ? (
        <p className="text-brand-panel-muted mt-3 text-sm">Loading…</p>
      ) : noMembersCount > 0 ? (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center gap-3 rounded-lg bg-white/10 p-3">
            <span className="bg-primary/25 font-heading grid size-9 shrink-0 place-items-center rounded-[11px] text-base font-bold text-white">
              {noMembersCount}
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">Firms with no members yet</span>
              <span className="text-brand-panel-muted text-xs">
                No seats filled — nobody can work these firms
              </span>
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            className="text-brand-panel w-full bg-white hover:bg-white/90"
            onClick={onReview}
          >
            Review firms with no members
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-3 rounded-lg bg-white/10 p-3">
          <span className="bg-success/25 grid size-9 shrink-0 place-items-center rounded-[11px] text-white">
            <Check className="size-4" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">Every firm has members</span>
            <span className="text-brand-panel-muted text-xs">Nothing needs attention right now</span>
          </span>
        </div>
      )}
    </div>
  );
}

/** Token-only (no chart lib) bar chart of firms joined per week. */
function FirmsJoinedChart({ weeks }: { weeks: number[] }) {
  const max = Math.max(...weeks, 1);
  return (
    <div className="flex h-20 items-end gap-1.5" aria-hidden="true">
      {weeks.map((v, i) => (
        <div
          key={i}
          className={cn(
            "flex-1 rounded-t-sm",
            i === weeks.length - 1 ? "bg-primary" : v === max && v > 0 ? "bg-info" : "bg-accent",
          )}
          style={{ height: `${v > 0 ? Math.max((v / max) * 100, 8) : 2}%` }}
        />
      ))}
    </div>
  );
}

function NewestFirmCard({
  bp,
  onOpenTeam,
}: {
  bp: BusinessPractitioner;
  onOpenTeam: () => void;
}) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        Newest firm
      </span>
      <div className="flex items-center gap-3">
        <Avatar size="lg">
          <AvatarFallback className="bg-info-soft text-info font-heading text-sm font-bold">
            {initials(bp.name)}
          </AvatarFallback>
        </Avatar>
        <span className="flex flex-col leading-tight">
          <span className="font-medium">{bp.name}</span>
          <span className="text-muted-foreground text-xs">
            Joined {joinedLabel(bp.created_at)} · {bp.member_count}{" "}
            {bp.member_count === 1 ? "member" : "members"}
          </span>
        </span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-1.5"
        onClick={onOpenTeam}
      >
        <Users className="size-4" />
        Open team
      </Button>
    </Card>
  );
}

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

/**
 * GA/SA team-management surface for a single BP firm: shows its combined team
 * (in-house + NSI-assigned, each tagged by origin), lets the operator unassign
 * an NSI member and assign a new NSI (house) user from the list of house members.
 */
function ManageTeamDialog({ bp, onClose }: { bp: BusinessPractitioner; onClose: () => void }) {
  const [team, setTeam] = useState<BpTeamMember[] | null>(null);
  const [houseMembers, setHouseMembers] = useState<Member[]>([]);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  // Guards the async assign/unassign handlers from setState-after-unmount when
  // the dialog is closed mid-request (the mount fetch below uses its own flag).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([getBpTeam(bp.id), listMembers()])
      .then(([t, m]) => {
        if (!active) return;
        setTeam(t);
        setHouseMembers(m);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [bp.id]);

  async function refreshTeam() {
    const t = await getBpTeam(bp.id);
    if (mounted.current) setTeam(t);
  }

  // House members not already assigned to this BP, and only those the current
  // user is allowed to assign (GA/SA any; CD → TL/Staff; TL → Staff), are the
  // assignable options — mirrors the backend's role-grant matrix.
  const currentUser = useAuthStore((s) => s.user);
  const assignedIds = new Set((team ?? []).map((m) => m.id));
  const assignable = houseMembers.filter(
    (m) => !assignedIds.has(m.id) && canAssignRole(currentUser, m.role),
  );

  async function handleAssign() {
    if (!selectedId) return;
    setAssigning(true);
    try {
      await assignNsiMember(bp.id, selectedId);
      await refreshTeam();
      if (mounted.current) setSelectedId("");
      toast.success("NSI member assigned.");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't assign this NSI member."));
    } finally {
      if (mounted.current) setAssigning(false);
    }
  }

  async function handleUnassign(member: BpTeamMember) {
    setBusyId(member.id);
    try {
      await unassignNsiMember(bp.id, member.id);
      await refreshTeam();
      toast.success(`${member.name || member.email} unassigned.`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't unassign this NSI member."));
    } finally {
      if (mounted.current) setBusyId(null);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Team — {bp.name}</DialogTitle>
          <DialogDescription>
            This firm&apos;s in-house members plus the NSI members assigned to it.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="text-destructive text-sm">Couldn&apos;t load this team.</p>
        ) : (
          <div className="flex flex-col gap-5">
            <Card className="gap-0 overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!team ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground text-center">
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : team.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground text-center">
                        No team members yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    team.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">
                          <span className="flex flex-col">
                            <span className="flex items-center gap-1.5">
                              {m.name || "—"}
                              {m.bp?.code && (
                                <Badge variant="outline" className="font-normal">
                                  {m.bp.code}
                                </Badge>
                              )}
                            </span>
                            <span className="text-muted-foreground text-xs font-normal">
                              {m.email}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell>
                          {m.origin === "nsi_assigned" ? (
                            <span className="flex flex-col gap-0.5">
                              <Badge variant="secondary" className="w-fit">
                                NSI
                              </Badge>
                              {m.home_org && (
                                <span className="text-muted-foreground text-xs">
                                  {m.home_org.name}
                                </span>
                              )}
                            </span>
                          ) : (
                            <Badge variant="outline">In-house</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.origin === "nsi_assigned" && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label={`Remove ${m.name || m.email}`}
                              disabled={busyId === m.id}
                              onClick={() => handleUnassign(m)}
                            >
                              <X className="size-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>

            <div className="flex items-end gap-2">
              <div className="grid flex-1 gap-2">
                <span className="text-sm font-medium">Assign NSI member</span>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger className="w-full" aria-label="Assign NSI member">
                    <SelectValue placeholder="Select a house member…" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignable.length === 0 ? (
                      <div className="text-muted-foreground px-2 py-1.5 text-sm">
                        No more NSI members to assign.
                      </div>
                    ) : (
                      assignable.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name || m.email}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                disabled={!selectedId || assigning}
                onClick={handleAssign}
              >
                {assigning ? "Assigning…" : "Assign"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const createSchema = Yup.object({
  name: Yup.string().trim().required("Firm name is required"),
  code: Yup.string().trim().max(12, "Keep the code to 12 characters or fewer"),
  adminName: Yup.string().trim(),
  adminEmail: Yup.string().email("Enter a valid email").required("Admin email is required"),
  adminIsPractitioner: Yup.boolean(),
});

interface CreateValues {
  name: string;
  code: string;
  adminName: string;
  adminEmail: string;
  adminIsPractitioner: boolean;
}

function CreateBusinessPractitionerDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void | Promise<void>;
}) {
  // NSI overseers this BP can be assigned to on creation: house Country
  // Directors plus Global/Super Admins (who oversee every BP anyway).
  const [overseers, setOverseers] = useState<Member[]>([]);
  const [cdIds, setCdIds] = useState<string[]>([]);

  useEffect(() => {
    // Clear the selection when the dialog closes so a cancelled selection can't
    // silently carry into the next BP you create (the dialog stays mounted).
    if (!open) {
      setCdIds([]);
      return;
    }
    let active = true;
    listMembers()
      .then(
        (members) =>
          active &&
          setOverseers(
            members.filter(
              (m) =>
                m.status === "active" &&
                (m.role === "country_director" || m.isSuperuser || m.isPlatformAdmin),
            ),
          ),
      )
      .catch(() => {
        // Non-fatal: the overseer assignment field just stays empty.
      });
    return () => {
      active = false;
    };
  }, [open]);

  function toggleCd(id: string) {
    setCdIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  async function handleSubmit(values: CreateValues, { setErrors }: FormikHelpers<CreateValues>) {
    try {
      await createBusinessPractitioner({
        name: values.name,
        code: values.code.trim() || undefined,
        admin_name: values.adminName || undefined,
        admin_email: values.adminEmail,
        admin_is_practitioner: values.adminIsPractitioner,
        country_director_ids: cdIds.length ? cdIds.map(Number) : undefined,
      });
      await onCreated();
      setCdIds([]);
      onOpenChange(false);
      toast.success("Business Practitioner created — an invite was emailed to its director.");
    } catch (err) {
      toast.error(
        applyApiErrors(
          err,
          { setErrors },
          {
            fieldMap: {
              admin_name: "adminName",
              admin_email: "adminEmail",
              admin_is_practitioner: "adminIsPractitioner",
            },
            fallback: "Couldn't create this Business Practitioner.",
          },
        ),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Business Practitioner</DialogTitle>
          <DialogDescription>
            Creates the firm and emails its first Country Director an invitation to set their
            password.
          </DialogDescription>
        </DialogHeader>
        <Formik<CreateValues>
          initialValues={{
            name: "",
            code: "",
            adminName: "",
            adminEmail: "",
            adminIsPractitioner: false,
          }}
          validationSchema={createSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Firm name" />
              <div className="grid gap-1">
                <FormField name="code" label="Short code (optional)" />
                <p className="text-muted-foreground text-xs">
                  A short identifier (e.g. “MVB”) shown next to this firm’s team members.
                </p>
              </div>
              <FormField name="adminName" label="Admin name (optional)" />
              <FormField name="adminEmail" label="Admin email" type="email" />
              <CheckboxField
                name="adminIsPractitioner"
                label="Admin is also a practitioner"
                hint="Also create a linked practitioner record for the director."
              />
              {overseers.length > 0 && (
                <div className="grid gap-2">
                  <span className="text-sm font-medium">Assign overseer(s)</span>
                  <p className="text-muted-foreground text-xs">
                    The house Country Directors (and Global/Super Admins) who oversee this firm. A
                    Country Director can then delegate it to their Team Leaders and Staff.
                  </p>
                  <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                    {overseers.map((m) => {
                      const kind = m.isSuperuser
                        ? "Global Admin"
                        : m.isPlatformAdmin
                          ? "Super Admin"
                          : "Country Director";
                      return (
                        <label
                          key={m.id}
                          className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="accent-primary size-4"
                            checked={cdIds.includes(m.id)}
                            onChange={() => toggleCd(m.id)}
                          />
                          <span className="flex min-w-0 flex-col leading-tight">
                            <span className="flex items-center gap-1.5">
                              <span className="truncate">{m.name || m.email}</span>
                              <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
                                {kind}
                              </span>
                            </span>
                            {m.name && (
                              <span className="text-muted-foreground truncate text-xs">
                                {m.email}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
